import { Box } from '@mui/material';
import { ApptRequestIvStatusButtonFactory } from 'apps/wrapper/src/components/insurance-verification/appt-request-flow/status-button/status-button-factory';
import { PatientIvStatusButtonFactory } from 'apps/wrapper/src/components/insurance-verification/patient-flow/status-button/status-button-factory';
import { useFeatureFlags } from 'apps/wrapper/src/hooks/feature-flags/use-feature-flags';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import moment from 'moment';
import { FC, useMemo } from 'react';
import { useApptRequestsIv } from '../../../../../hooks/use-appt-requests-iv';
import { useApptRequestsWidget } from '../../../../../hooks/use-appt-requests-widget';
import { useRequestDetailsView } from '../../../../../hooks/use-request-details-view';
import { LabeledText } from '../../../../../shared/labeled-text/labeled-text';

export const IvStatus: FC = (props) => {
  const { selectedLocation } = useLocations();
  const { hasFeature } = useFeatureFlags();
  const { locationId } = useApptRequestsWidget();
  const { apptRequestIvSummaries, patientIvSummaries, ivStatusesToProcess } = useApptRequestsIv();
  const { requestDetails } = useRequestDetailsView();
  const isWidgetRequest = useMemo(() => !requestDetails.isLeadsRequest && !requestDetails.milaCaseId, [requestDetails]);
  // A lead that skipped insurance has no carrier on the appointment request, so
  // there's nothing to verify — surface it as "Unknown" rather than defaulting to
  // "Verification In Process".
  const hasInsuranceInfo = !!requestDetails.insuranceInfo?.carrier;

  if (!selectedLocation) return null;

  if (!hasFeature(selectedLocation.id, 'InsuranceVerification2')) return null;

  if (!ivStatusesToProcess.includes(requestDetails.status)) return null;

  const patientIvSummary = useMemo(() => {
    if (
      !requestDetails ||
      !requestDetails.patient?.id ||
      (requestDetails?.isDirectlyBooked && requestDetails?.isNewPatientCreated)
    )
      return null;

    if (patientIvSummaries.length === 0) return null;

    return patientIvSummaries.find((p) => p.patientId === requestDetails.patient.id);
  }, [patientIvSummaries, requestDetails.patient?.id]);

  const apptRequestIvSummary = useMemo(() => {
    if (!requestDetails || !requestDetails.id) return null;

    if (apptRequestIvSummaries.length === 0) return null;

    return apptRequestIvSummaries.find((p) => p.appointmentRequestId === requestDetails.id);
  }, [apptRequestIvSummaries, requestDetails.id]);

  const createdDate = moment(requestDetails.createdDate);
  const twoHoursAgo = moment().subtract(2, 'hours');

  const isVerificationExpired = useMemo(
    () => !patientIvSummary && !apptRequestIvSummary && !createdDate.isAfter(twoHoursAgo, 'hours'),
    [patientIvSummary, apptRequestIvSummary, createdDate, twoHoursAgo]
  );

  return (
    <LabeledText label="Insurance">
      <Box sx={{ marginLeft: '10px' }}>
        {requestDetails.patient?.id && patientIvSummary && (
          <PatientIvStatusButtonFactory
            patientId={requestDetails.patient.id}
            locationId={locationId}
            verificationPriority="Primary"
            patientSummary={patientIvSummary}
            selectedSummary={patientIvSummary.primaryVerificationSummary}
          />
        )}
        {(!requestDetails.patient?.id || (requestDetails.isDirectlyBooked && requestDetails.isNewPatientCreated)) && (
          <ApptRequestIvStatusButtonFactory
            apptRequestId={requestDetails.id}
            locationId={locationId}
            ivSummary={apptRequestIvSummary?.verificationSummary || undefined}
            isUnknown={
              !hasInsuranceInfo ||
              isVerificationExpired ||
              (isWidgetRequest && !apptRequestIvSummary?.verificationSummary)
            }
          />
        )}
      </Box>
    </LabeledText>
  );
};
